import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button, Card, Descriptions, Input, Spin, Table, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

import { FaFileInvoiceDollar, FaMoneyBillWave, FaReceipt, FaSearch, FaUser } from 'react-icons/fa';
import { MdOutlineKeyboardArrowLeft } from "react-icons/md";

import '../../../css/UserAdmin/Global.css';
import '../../../css/UserAdmin/ViewDetails.css';

import Footer from '../Footer';
import Header from '../NewHeader';
import SideNav from '../SideNav';

const { Text } = Typography;

const UserLedgerRecord = () => {
    const navigate = useNavigate();
    const [isSideNavOpen, setIsSideNavOpen] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);
    const params = useParams();
    const studentIdFromUrl = params.id;

    const [studentEnrollment, setStudentEnrollment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // State for transaction search within the ledger detail page
    const [transactionSearchTerm, setTransactionSearchTerm] = useState('');

    const toggleSideNav = useCallback(() => setIsSideNavOpen(prev => !prev), []);

    useEffect(() => {
        const handleResize = () => setIsSmallScreen(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // --- Fetch Student's Full Enrollment Record ---
    const fetchStudentLedgerDetails = useCallback(async () => {
        // ... (existing fetch logic - no changes here)
        if (!studentIdFromUrl) {
            setError("Student ID is missing from URL.");
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        setStudentEnrollment(null);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/enrollments/${studentIdFromUrl}`);
            if (!response.ok) {
                let errMsg = `Failed to fetch student ledger details. Status: ${response.status}`;
                try { const errData = await response.json(); errMsg = errData.message || errMsg; }
                catch (e) { const textError = await response.text(); errMsg += ` - ${textError}`; }
                throw new Error(errMsg);
            }
            const result = await response.json();
            const dataToSet = result.data ? result.data : result;
            if (!dataToSet || typeof dataToSet !== 'object' || !dataToSet._id) {
                throw new Error("Invalid student data received from server.");
            }
            setStudentEnrollment(dataToSet);
        } catch (err) {
            console.error("Error fetching student ledger details:", err);
            setError(err.message);
            toast.error(err.message || "Could not load student ledger details.");
        } finally {
            setLoading(false);
        }
    }, [studentIdFromUrl]);

    useEffect(() => {
        if (studentIdFromUrl) {
            fetchStudentLedgerDetails();
        } else {
            setLoading(false);
            setError("No Student ID provided in the URL.");
        }
    }, [studentIdFromUrl, fetchStudentLedgerDetails]);


    const handleBack = () => navigate('/admin/manage-user-ledger');

    // --- Calculations and Data Preparation for Display ---
    const paymentInfo = studentEnrollment?.paymentInfo;
    const tuitionAndFees = paymentInfo?.tuitionAndFees;
    const totalAssessed = tuitionAndFees?.totalAmount || 0;
    const amountPaid = paymentInfo?.paymentAmount || 0;
    const balance = totalAssessed - amountPaid;

    // Memoize the generation of transactions list
    const allTransactions = useMemo(() => {
        if (!paymentInfo) return [];
        const transactions = [];
        if (paymentInfo.paymentAmount > 0 && (paymentInfo.paymentType === 'downpayment' || paymentInfo.paymentType === 'full' || paymentInfo.isFullPayment)) {
            transactions.push({
                key: `initial-${paymentInfo.referenceNumber}`,
                date: paymentInfo.paymentDate,
                description: paymentInfo.paymentType === 'full' ? 'Full Payment' : 'Initial Downpayment',
                reference: paymentInfo.referenceNumber,
                method: paymentInfo.paymentMethod,
                amount: paymentInfo.paymentAmount,
                type: 'Payment',
                status: 'Paid', // Initial payment is considered paid
            });
        }
        (paymentInfo.remainingPayments || []).forEach((payment, index) => {
            transactions.push({
                key: `due-${payment._id || `due-${index}`}`, // More unique key
                date: payment.dueDate,
                description: payment.description || `Installment ${payment.installmentNumber || index + 1} Due`,
                reference: paymentInfo.referenceNumber,
                amount: payment.amount,
                type: 'Due',
                status: payment.status || 'Pending',
            });
            if (payment.status && payment.status.toLowerCase() === 'paid') {
                transactions.push({
                    key: `paid-${payment._id || `paid-${index}`}`, // More unique key
                    date: payment.paymentDate || payment.dueDate,
                    description: payment.description || `Payment for Installment ${payment.installmentNumber || index + 1}`,
                    reference: payment.transactionRef || paymentInfo.referenceNumber,
                    method: paymentInfo.paymentMethod,
                    amount: payment.amount,
                    type: 'Payment',
                    status: 'Paid',
                });
            }
        });
        return transactions.sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());
    }, [paymentInfo]); // Re-calculate only if paymentInfo changes

    // Filter transactions based on transactionSearchTerm
    const filteredTransactions = useMemo(() => {
        if (!transactionSearchTerm) return allTransactions;
        const lowerSearch = transactionSearchTerm.toLowerCase();
        return allTransactions.filter(t =>
            (t.description || '').toLowerCase().includes(lowerSearch) ||
            (t.reference || '').toLowerCase().includes(lowerSearch) ||
            (t.type || '').toLowerCase().includes(lowerSearch) ||
            (t.status || '').toLowerCase().includes(lowerSearch) ||
            (t.amount?.toString() || '').includes(lowerSearch) // Search amount as string
        );
    }, [allTransactions, transactionSearchTerm]);


    const transactionColumns = [
        { title: 'Date', dataIndex: 'date', key: 'date', render: (text) => dayjs(text).isValid() ? dayjs(text).format('YYYY-MM-DD HH:mm') : 'N/A', width: 150, sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix() },
        { title: 'Description', dataIndex: 'description', key: 'description', sorter: (a, b) => (a.description || '').localeCompare(b.description || '') },
        { title: 'Reference', dataIndex: 'reference', key: 'reference', width: 200, sorter: (a, b) => (a.reference || '').localeCompare(b.reference || '') },
        { title: 'Type', dataIndex: 'type', key: 'type', width: 100, render: (type) => <Tag color={type === 'Payment' ? 'success' : 'warning'}>{type}</Tag>, sorter: (a, b) => (a.type || '').localeCompare(b.type || '') },
        { title: 'Amount', dataIndex: 'amount', key: 'amount', align: 'right', render: (amount) => amount?.toFixed(2) || '0.00', width: 120, sorter: (a, b) => (a.amount || 0) - (b.amount || 0) },
        { title: 'Status', dataIndex: 'status', key: 'status', width: 100, render: (status) => status ? <Tag color={status === 'Paid' ? 'green' : 'gold'}>{status}</Tag> : null, sorter: (a, b) => (a.status || '').localeCompare(b.status || '') },
    ];


    // --- Conditional Rendering ---
    if (loading) { /* ... as before ... */
        return (
            <div className="main main-container"><Header />
                <div className="main-content loading-container">
                    <Spin size="large" /><p className="loading-text">Loading Student Ledger...</p>
                </div><Footer />
            </div>
        );
    }
    if (error) { /* ... as before ... */
        return (
            <div className="main main-container"><Header />
                <div className="main-content">
                    <div className="page-title">
                        <div className="arrows" onClick={handleBack} role="button" tabIndex={0}><MdOutlineKeyboardArrowLeft /></div>
                        <p className="heading">Error</p>
                    </div>
                    <div className="error-message-container"><p>{error}</p>
                        <Button onClick={fetchStudentLedgerDetails}>Try Again</Button>
                    </div>
                </div><Footer />
            </div>
        );
    }
    if (!studentEnrollment) { /* ... as before ... */
        return (
            <div className="main main-container"><Header />
                <div className="main-content">
                    <div className="page-title">
                        <div className="arrows" onClick={handleBack} role="button" tabIndex={0}><MdOutlineKeyboardArrowLeft /></div>
                        <p className="heading">Student Ledger Not Found</p>
                    </div>
                    <p style={{ textAlign: 'center' }}>The requested student ledger could not be found.</p>
                </div><Footer />
            </div>
        );
    }

    return (
        <div className={`admin-page-container ${isSideNavOpen ? 'admin-sidenav-open' : ''}`}>
            <Header onMenuClick={toggleSideNav} isSideNavOpen={isSideNavOpen} />
            <div className="admin-content-wrapper">
                <div className="admin-sidenav-container">
                    <SideNav isOpen={isSideNavOpen} onClose={toggleSideNav} isSmallScreen={isSmallScreen} />
                </div>
                <main className="admin-main-content-area">
                    <div className="page-title">
                        <div className="arrows" onClick={handleBack} role="button" tabIndex={0} onKeyPress={handleBack}>
                            <MdOutlineKeyboardArrowLeft />
                        </div>
                        <p className="heading">User Ledger Details</p>
                    </div>

                    <Card className="details-card section-gap" title={<><FaUser className="section-icon" />Student Information</>}>
                        <Descriptions bordered column={1} size="small" layout="horizontal">
                            <Descriptions.Item label="Student ID">{studentEnrollment.studentID || 'N/A'}</Descriptions.Item>
                            <Descriptions.Item label="Full Name">{studentEnrollment.fullName || 'N/A'}</Descriptions.Item>
                            <Descriptions.Item label="Email">{studentEnrollment.email || 'N/A'}</Descriptions.Item>
                            <Descriptions.Item label="Grade & Section">{`${studentEnrollment.academicLevel || 'N/A'} - ${studentEnrollment.section || 'N/A'} (${studentEnrollment.academicStrand || 'N/A'})`}</Descriptions.Item>
                            <Descriptions.Item label="Academic Year">{studentEnrollment.academicYear || 'N/A'}</Descriptions.Item>
                            <Descriptions.Item label="Term">{studentEnrollment.academicTerm || 'N/A'}</Descriptions.Item>
                        </Descriptions>
                    </Card>

                    <Card className="details-card section-gap" title={<><FaFileInvoiceDollar className="section-icon" />Account Summary</>}>
                        <Descriptions bordered column={1} size="small" layout="horizontal">
                            <Descriptions.Item label="Total Assessed Fees">{totalAssessed.toFixed(2)}</Descriptions.Item>
                            <Descriptions.Item label="Total Amount Paid">{amountPaid.toFixed(2)}</Descriptions.Item>
                            <Descriptions.Item label="Current Balance">
                                <Text type={balance > 0 ? 'danger' : (balance === 0 ? 'success' : 'secondary')} strong>
                                    {balance.toFixed(2)}
                                </Text>
                                {balance === 0 && paymentInfo?.isFullPayment && <Tag color="green" style={{ marginLeft: 8 }}>Fully Paid</Tag>}
                                {balance > 0 && <Tag color="red" style={{ marginLeft: 8 }}>Outstanding</Tag>}
                                {balance < 0 && <Tag color="blue" style={{ marginLeft: 8 }}>Overpayment</Tag>}
                            </Descriptions.Item>
                            <Descriptions.Item label="Payment Mode">{paymentInfo?.paymentMode || 'N/A'}</Descriptions.Item>
                        </Descriptions>
                    </Card>

                    {tuitionAndFees && (
                        <Card className="details-card section-gap" title={<><FaMoneyBillWave className="section-icon" />Tuition & Fees Breakdown</>}>
                            <Descriptions bordered column={{ xxl: 2, xl: 2, lg: 1, md: 1, sm: 1, xs: 1 }} size="small" layout="horizontal">
                                <Descriptions.Item label="Tuition Fee">{tuitionAndFees.tuition?.toFixed(2) || '0.00'}</Descriptions.Item>
                                <Descriptions.Item label="Miscellaneous Fees">{tuitionAndFees.miscellaneous?.toFixed(2) || '0.00'}</Descriptions.Item>
                                <Descriptions.Item label="Other Fees">{tuitionAndFees.otherFees?.toFixed(2) || '0.00'}</Descriptions.Item>
                                <Descriptions.Item label="Energy Fee">{tuitionAndFees.energyFee?.toFixed(2) || '0.00'}</Descriptions.Item>
                                <Descriptions.Item label="Voucher Deduction">{tuitionAndFees.voucherDeduction?.toFixed(2) || '0.00'}</Descriptions.Item>
                                <Descriptions.Item label="Total Assessed (Breakdown)">{tuitionAndFees.totalAmount?.toFixed(2) || '0.00'}</Descriptions.Item>
                            </Descriptions>
                        </Card>
                    )}

                    <Card className="details-card section-gap" title={<><FaReceipt className="section-icon" />Payment Transactions & Due Dates</>}>
                        <Input
                            placeholder="Search Transactions (Description, Reference, Type, Status, Amount)"
                            allowClear
                            suffix={<FaSearch style={{ color: '#aaa' }} />}
                            value={transactionSearchTerm}
                            onChange={(e) => setTransactionSearchTerm(e.target.value)}
                            style={{ marginBottom: 16, width: '100%', maxWidth: 400 }}
                        />
                        {filteredTransactions.length > 0 ? ( // Use filteredTransactions for Table dataSource
                            <Table
                                columns={transactionColumns}
                                dataSource={filteredTransactions} // Use filtered data
                                pagination={{ pageSize: 5, showSizeChanger: true, pageSizeOptions: ['5', '10', '20'] }}
                                size="small"
                                rowKey="key" // Ensure keys are unique
                                scroll={{ x: 800 }}
                                bordered
                            />
                        ) : (
                            <p>{transactionSearchTerm ? "No transactions match your search." : "No payment transactions or installment plan found."}</p>
                        )}
                    </Card>
                </main>
            </div>
            <Footer />
        </div>
    );
};

export default UserLedgerRecord;